<template>
  <div class="certifications">
    <vs-collapse type="border">
      <vs-collapse-item-m open>
        <div slot="header">Filters</div>
        <div class="flex w-full pb-5 searcheable-fields">
          <div class="w-2/3 pr-3">
            <label class="vs-input--label">Certification name</label>
            <vs-input
              class="w-full"
              v-model="filters.name"
              @keyup.enter.native="applyFilters"
            />
          </div>
          <div class="w-1/3 pl-3">
            <label class="vs-input--label">Status</label>
            <v-select
              v-model="filters.status"
              :clearable="false"
              :reduce="(option) => option.value"
              :options="statusOptions"
            />
          </div>
        </div>
        <div class="w-full certifications__action-buttons">
          <vs-button class="mt-2" type="border" @click="clearFilters">
            Clear
          </vs-button>
          <vs-button class="mt-2 ml-2" @click="applyFilters">
            Search
          </vs-button>
        </div>
      </vs-collapse-item-m>
    </vs-collapse>

    <content-list-table
      :items="items"
      :typeSelected="typeSelected"
      :pageSize="pageSize"
      :loading="isLoading"
      :columnsByType="columnsByType"
      :cellStylesByType="cellStylesByType"
      :navigateOnSelect="true"
      :editRouteResolver="resolveEditRoute"
    >
      <template #name-cell="{ item }">
        <certification-name-cell :name="item.name" :isDraft="item.isDraft" />
      </template>
    </content-list-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import omitBy from 'lodash.omitby'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import ContentListTable from './components/ContentListTable.vue'
import CertificationNameCell from './components/CertificationNameCell.vue'
import CONSTANTS from '@/constants'
import { resolveEditPathByType } from '@/utils/navigationHelper'
import { CERT_LIST_STYLES } from '@/constants/certifications'

export default {
  name: 'certifications',
  components: {
    CertificationNameCell,
    ContentListTable,
    'v-select': vSelect,
  },
  data: () => ({
    typeSelected: 'CertificationVersions',
    pageSize: 25,
    isLoading: false,
    filters: {
      name: '',
      status: 'all',
    },
    columnsByType: {
      CertificationVersions: [
        'name',
        'version',
        'status',
        'modified',
        'modified_by',
      ],
    },
    cellStylesByType: CERT_LIST_STYLES,
    statusOptions: [
      { label: 'All', value: 'all' },
      { label: 'Draft', value: CONSTANTS.CERTIFICATION_STATUSES.DRAFT },
      { label: 'Published', value: CONSTANTS.CERTIFICATION_STATUSES.PUBLISHED },
      {
        label: 'Unpublished',
        value: CONSTANTS.CERTIFICATION_STATUSES.UNPUBLISHED,
      },
    ],
  }),
  computed: {
    ...mapGetters('certification', ['getCertificationVersions']),
    items() {
      const normalizedRows = this.normalizeCertificationRows(
        this.getCertificationVersions || []
      )
      return {
        CertificationVersions: {
          items: normalizedRows,
          totalItems: normalizedRows.length,
        },
      }
    },
  },
  methods: {
    ...mapActions('certification', ['listCertificationVersionsAction']),
    async searchCertificationsVersions(payload = {}) {
      try {
        this.isLoading = true
        await this.listCertificationVersionsAction(payload)
      } catch (error) {
        this.showError(error)
      } finally {
        this.isLoading = false
      }
    },
    buildApiFilters() {
      const filters = {}
      const name = this.filters.name.trim()

      if (name) filters.name = name
      if (this.filters.status !== 'all') filters.status = this.filters.status

      return filters
    },
    buildRouteQuery() {
      return this.buildApiFilters()
    },
    applyFilters() {
      const query = omitBy(this.buildRouteQuery(), isEmpty)

      this.searchCertificationsVersions(this.buildApiFilters())

      if (!isEqual(this.$route.query, query)) {
        this.$router.replace({
          name: 'certifications',
          query,
        })
      }
    },
    clearFilters() {
      this.filters.name = ''
      this.filters.status = 'all'
      this.applyFilters()
    },
    async restoreQuerySearch() {
      const query = this.$route.query

      this.filters.name = query.name || ''
      this.filters.status = query.status || 'all'

      await this.searchCertificationsVersions(this.buildApiFilters())
    },
    resolveEditRoute(item) {
      const name = resolveEditPathByType(this.typeSelected, this.$router)
      if (!name) return {}
      return {
        name,
        params: { id: item.certification_id, name: item.name },
      }
    },

    normalizeCertificationRows(rows = []) {
      const versions = []
      rows.forEach((item) => {
        versions.push({
          id: item.latest.exam_id ? item.latest.exam_id : `latest-${item.certification_id}`,
          certification_id: item.certification_id,
          name: item.latest.certification_short_name,
          version: item.latest.version_label,
          status: item.latest.status,
          modified: item.latest.modified,
          modified_by: item.latest.modified_by_name || item.latest.modified_by,
          isDraft: false,
          metadata: {},
        })
        if (item.draft) {
          versions.push({
            id: item.draft.exam_id ? item.draft.exam_id : `draft-${item.certification_id}`,
            certification_id: item.certification_id,
            name: item.draft.certification_short_name,
            version: item.draft.version_label,
            status: item.draft.status,
            modified: item.draft.modified,
            modified_by: item.draft.modified_by_name || item.draft.modified_by,
            isDraft: true,
            metadata: {},
          })
        }
      })
      return versions
    },
  },
  async mounted() {
    if (!isEmpty(this.$route.query)) {
      await this.restoreQuerySearch()
      return
    }

    await this.searchCertificationsVersions()
  },
  watch: {
    '$route.query': {
      async handler(query) {
        if (isEqual(query, omitBy(this.buildRouteQuery(), isEmpty))) return

        await this.restoreQuerySearch()
      },
    },
  },
}
</script>

<style scoped>
.certifications {
  padding: 20px;
}
.certifications__action-buttons {
  display: flex;
  justify-content: flex-end;
}
</style>
